import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExperienceSection } from "@/types/experience-section";
import useFetchingEventTags from "@/hooks/useFetchingEventTags";
import { slugifyTitle } from "@/hooks/useExperienceSections";

type ExperienceSectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section?: ExperienceSection | null;
  onSave: (data: Partial<ExperienceSection>) => void;
  isLoading?: boolean;
};

const SECTION_TYPES = [
  { value: "tag_based", label: "Tag-based (filters by event tag)" },
  { value: "archived", label: "Archived/Recent events" },
];

const ExperienceSectionDialog = ({
  open,
  onOpenChange,
  section,
  onSave,
  isLoading,
}: ExperienceSectionDialogProps) => {
  const { availableTags } = useFetchingEventTags();

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [icon, setIcon] = useState("");
  const [tag, setTag] = useState<string>("");
  const [sectionType, setSectionType] = useState<string>("tag_based");
  const [isActive, setIsActive] = useState(true);
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [enableInterestSignup, setEnableInterestSignup] = useState(true);

  type EBMode = "inherit" | "custom" | "disabled";
  const [ebMode, setEbMode] = useState<EBMode>("inherit");
  const [ebDays, setEbDays] = useState<string>("");
  const [ebPct, setEbPct] = useState<string>("");

  type BFMode = "inherit" | "enabled" | "disabled";
  const [bfMode, setBfMode] = useState<BFMode>("inherit");

  useEffect(() => {
    if (section) {
      setTitle(section.title);
      setSubtitle(section.subtitle || "");
      setIcon(section.icon || "");
      setTag(section.tag || "");
      setSectionType(section.section_type);
      setIsActive(section.is_active);
      setSlug(section.slug || "");
      setSlugTouched(true);
      setEnableInterestSignup(section.enable_interest_signup ?? true);

      if (section.early_bird_enabled === false) {
        setEbMode("disabled");
      } else if (
        section.early_bird_enabled === true ||
        section.early_bird_days_before != null ||
        section.early_bird_discount_percent != null
      ) {
        setEbMode("custom");
      } else {
        setEbMode("inherit");
      }
      setEbDays(section.early_bird_days_before?.toString() ?? "");
      setEbPct(section.early_bird_discount_percent?.toString() ?? "");
      setBfMode(
        section.bring_friend_eligible === true
          ? "enabled"
          : section.bring_friend_eligible === false
            ? "disabled"
            : "inherit",
      );
    } else {
      setTitle("");
      setSubtitle("");
      setIcon("");
      setTag("");
      setSectionType("tag_based");
      setIsActive(true);
      setSlug("");
      setSlugTouched(false);
      setEnableInterestSignup(true);
      setEbMode("inherit");
      setEbDays("");
      setEbPct("");
      setBfMode("inherit");
    }
  }, [section, open]);

  // Auto-fill slug from title until the user edits it.
  useEffect(() => {
    if (!slugTouched) {
      setSlug(slugifyTitle(title));
    }
  }, [title, slugTouched]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let early_bird_enabled: boolean | null = null;
    let early_bird_days_before: number | null = null;
    let early_bird_discount_percent: number | null = null;
    if (ebMode === "disabled") {
      early_bird_enabled = false;
    } else if (ebMode === "custom") {
      early_bird_enabled = true;
      early_bird_days_before = ebDays !== "" ? Number(ebDays) : null;
      early_bird_discount_percent = ebPct !== "" ? Number(ebPct) : null;
    }

    const bring_friend_eligible: boolean | null =
      bfMode === "enabled" ? true : bfMode === "disabled" ? false : null;

    onSave({
      ...(section?.id && { id: section.id }),
      title,
      subtitle: subtitle || null,
      icon: icon || null,
      tag: sectionType === "tag_based" ? tag : null,
      section_type: sectionType as ExperienceSection["section_type"],
      is_active: isActive,
      slug: slug || slugifyTitle(title),
      enable_interest_signup: enableInterestSignup,
      early_bird_enabled,
      early_bird_days_before,
      early_bird_discount_percent,
      bring_friend_eligible,
    });
  };

  const isEdit = !!section;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Section" : "Add New Section"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Nimi Classics"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">URL slug</Label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value);
              }}
              placeholder="auto-generated from title"
            />
            <p className="text-xs text-muted-foreground">
              Used in direct links: <code>/interest/{slug || "your-slug"}</code>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="icon">Icon/Emoji</Label>
            <Input
              id="icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="e.g., ✨ or 🏃‍♀️💨"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="subtitle">Subtitle/Description</Label>
            <Textarea
              id="subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="Brief description of this section..."
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="section-type">Section Type</Label>
            <Select value={sectionType} onValueChange={setSectionType}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {SECTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {sectionType === "tag_based" && (
            <div className="space-y-2">
              <Label htmlFor="tag">Event Tag</Label>
              <Select value={tag} onValueChange={setTag}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a tag" />
                </SelectTrigger>
                <SelectContent>
                  {availableTags.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="enable-interest">Allow interest sign-ups</Label>
              <p className="text-xs text-muted-foreground">
                Show the "Notify me" bell on this section.
              </p>
            </div>
            <Switch
              id="enable-interest"
              checked={enableInterestSignup}
              onCheckedChange={setEnableInterestSignup}
              disabled={sectionType === "archived"}
            />
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <Label>Early Bird Pricing</Label>
            <Select value={ebMode} onValueChange={(v) => setEbMode(v as "inherit" | "custom" | "disabled")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">Inherit global default</SelectItem>
                <SelectItem value="custom">Custom for this series</SelectItem>
                <SelectItem value="disabled">Disabled for this series</SelectItem>
              </SelectContent>
            </Select>
            {ebMode === "custom" && (
              <div className="grid grid-cols-2 gap-2 pt-2">
                <div className="space-y-1">
                  <Label htmlFor="eb-series-days" className="text-xs">Days before</Label>
                  <Input
                    id="eb-series-days"
                    type="number"
                    min={1}
                    placeholder="Inherit"
                    value={ebDays}
                    onChange={(e) => setEbDays(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="eb-series-pct" className="text-xs">Discount %</Label>
                  <Input
                    id="eb-series-pct"
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    placeholder="Inherit"
                    value={ebPct}
                    onChange={(e) => setEbPct(e.target.value)}
                  />
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Overrides apply per-field. Leave blank in Custom to inherit that field from global.
            </p>
          </div>

          <div className="space-y-2 rounded-lg border p-3">
            <Label>Bring a Friend for Free</Label>
            <Select
              value={bfMode}
              onValueChange={(v) =>
                setBfMode(v as "inherit" | "enabled" | "disabled")
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="inherit">
                  Inherit global default
                </SelectItem>
                <SelectItem value="enabled">
                  Enabled for this series
                </SelectItem>
                <SelectItem value="disabled">
                  Disabled for this series
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              When enabled, members who book tagged events can invite friends
              for free. Overridden by per-event settings.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="is-active">Active</Label>
            <Switch
              id="is-active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading || !title}>
              {isLoading ? "Saving..." : isEdit ? "Save Changes" : "Create Section"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ExperienceSectionDialog;
